import { DEFAULT_STEP_OPTIONS } from '../../constants/onboarding';
import { Flex, FlexProps } from '@ttoss/ui';
import { LatLng, useGeocoding } from './useGeocoding';
import { Steps } from 'intro.js-react';
import { useNavigation } from '../../providers/NavigationProvider/NavigationProvider';
import Header from '../Header/Header';
import Map, { MapProps } from '../Map/Map';
import React, { useState } from 'react';
import SubMenu, { SubMenuProps } from '../SubMenu/SubMenu';

export type { LatLng };

export type MapWithSearchProps = {
  initialLocation?: LatLng;
  onLatLng?: (value: LatLng) => void;
  image?: MapProps['image'];
  selectedSubMenu?: SubMenuProps['selected'];
  onClickSubMenu?: SubMenuProps['onClick'];
  sx?: FlexProps['sx'];
  children?: React.ReactNode;
  customSubMenu?: React.ReactNode;
  hideControllers?: boolean;
  hideSubMenu?: boolean;
  hideMap?: boolean;
  route?: string;
  markedPositions?: MapProps['markedPositions'];
};

export const ONBOARD_KEY_HOME = 'onboarding:MapWithSearch:Home';
export const ONBOARD_KEY_RECOMMENDATION =
  'onboarding:MapWithSearch:Recommendation';

const MapWithSearch = ({
  initialLocation,
  onLatLng,
  onClickSubMenu,
  selectedSubMenu,
  hideControllers,
  hideSubMenu,
  hideMap,
  customSubMenu,
  children,
  route,
  markedPositions,
  sx,
}: MapWithSearchProps) => {
  const [placeName, setPlaceName] = useState('');

  const [location, setLocation] = useState<LatLng | undefined>(initialLocation);

  const { getLatLong, getAddress } = useGeocoding();

  const handleEnter = async (text: string) => {
    try {
      const dataLocation = await getLatLong({ address: text });

      setLocation(dataLocation);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const onMarkPin = React.useCallback(
    async (latLng: LatLng) => {
      try {
        const address = await getAddress(latLng);

        setPlaceName(address as string);
        onLatLng?.(latLng);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('onMarkPin::error::', error);
      }
    },
    [getAddress, onLatLng]
  );

  const { popUpSpecialistWasClosed } = useNavigation();

  const [onboardingHome, setOnboardingHome] = React.useState({
    stepsEnabled: false,
    initialStep: 0,
    steps: [
      {
        intro:
          'Vamos fazer um breve tour pelas funcionalidades do Siflor Cerrado?',
      },
      {
        intro:
          'Para receber recomendações e outras informações, você deve selecionar a localização que deseja verificar',
      },
      {
        element: '.header-search-input',
        intro:
          'Você pode utilizar a Barra de Pesquisa para digitar o endereço ou localização geográfica, de sua propriedade',
      },
      {
        element: '.map',
        intro:
          'Encontre no mapa a localização de sua propriedade com apenas um clique',
      },
      {
        element: '.zoom-control',
        intro: 'Utilize o zoom no mapa para encontrar sua propriedade',
        position: 'top',
      },
      {
        element: '.current-location-button',
        intro: 'Clique aqui para localizar automaticamente seu endereço atual',
        position: 'top',
      },
    ],
  });

  const [onboardingRecommendation, setOnboardingRecommendation] =
    React.useState({
      stepsEnabled: false,
      initialStep: 0,
      steps: [
        {
          element: '.badge-menu-recommendation',
          intro:
            'Vamos verificar o nível de aptidão das espécies/clones para sua localização?',
        },
        {
          element: '.toggle-button',
          intro:
            'Este é o botão de seleção de sistema de cultivo, selecione o sistema mais indicado para você',
        },
      ],
    });

  React.useEffect(() => {
    const showOnboarding = !localStorage.getItem(ONBOARD_KEY_HOME);

    if (route === '/' && popUpSpecialistWasClosed && showOnboarding) {
      setTimeout(() => {
        setOnboardingHome((prev) => {
          return { ...prev, stepsEnabled: true };
        });
      }, 1000);
    }
  }, [popUpSpecialistWasClosed, route]);

  React.useEffect(() => {
    const showOnboarding = !localStorage.getItem(ONBOARD_KEY_RECOMMENDATION);

    if (
      route === '/' &&
      popUpSpecialistWasClosed &&
      showOnboarding &&
      !hideSubMenu
    ) {
      setTimeout(() => {
        setOnboardingRecommendation((prev) => {
          return {
            ...prev,
            stepsEnabled: true,
          };
        });
      }, 1000);
    }
  }, [route, popUpSpecialistWasClosed, hideSubMenu]);

  const onExitOnboardingHome = () => {
    setOnboardingHome((prev) => {
      return { ...prev, stepsEnabled: false };
    });

    localStorage.setItem(ONBOARD_KEY_HOME, 'showed');
  };

  const onExitOnboardingRecommendation = () => {
    setOnboardingRecommendation((prev) => {
      return { ...prev, stepsEnabled: false };
    });

    localStorage.setItem(ONBOARD_KEY_HOME, 'showed');
  };

  React.useEffect(() => {
    if (initialLocation) {
      onMarkPin(initialLocation);
    }
  }, [initialLocation, onMarkPin]);

  return (
    <Flex
      sx={{
        height: '100vh',
        width: '100%',
        flexDirection: 'column',
        position: 'relative',
        ...sx,
      }}
    >
      <Steps
        enabled={onboardingHome.stepsEnabled}
        steps={onboardingHome.steps}
        initialStep={onboardingHome.initialStep}
        onExit={onExitOnboardingHome}
        options={DEFAULT_STEP_OPTIONS}
      />

      <Steps
        enabled={onboardingRecommendation.stepsEnabled}
        steps={onboardingRecommendation.steps}
        initialStep={onboardingRecommendation.initialStep}
        onExit={onExitOnboardingRecommendation}
        options={DEFAULT_STEP_OPTIONS}
      />

      <Header onEnter={handleEnter} initialSearchText={placeName} />
      {!hideSubMenu && (
        <SubMenu selected={selectedSubMenu} onClick={onClickSubMenu} />
      )}

      {customSubMenu}

      {!hideMap && (
        <Flex sx={{ flex: 1 }}>
          <Map
            markedPositions={markedPositions}
            height="100%"
            width="100%"
            options={{ center: location }}
            onMarkPin={onMarkPin}
            hideControllers={hideControllers}
          />
        </Flex>
      )}

      {children}
    </Flex>
  );
};

export default MapWithSearch;
